import React, { useState, useEffect } from "react";
import "./index.css";
import api from './services/api';
import socketService from './services/socket';

// Helper function to get team logo URL (placeholder URLs - replace with actual team logos)
const getTeamLogo = (teamName) => {
  const logoMap = {
    "الأخضر": "https://via.placeholder.com/80x80/27ae60/ffffff?text=الأخضر",
    "الهلال": "https://via.placeholder.com/80x80/ffffff/000000?text=🌙",
    "الاتفاق": "https://via.placeholder.com/80x80/2980b9/ffffff?text=الاتفاق",
    "الاتحاد": "https://via.placeholder.com/80x80/ffffff/000000?text=⚽",
    "النصر": "https://via.placeholder.com/80x80/ffd700/000000?text=النصر",
    "الشباب": "https://via.placeholder.com/80x80/000000/ffffff?text=⚡",
  };
  return logoMap[teamName] || "https://via.placeholder.com/80x80/667eea/ffffff?text=Team";
};

export default function App() {
  const [screen, setScreen] = useState("landing");
  const [loginMethod, setLoginMethod] = useState("nafath"); // "nafath" or "muqeem"
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [seatInfo, setSeatInfo] = useState({
    zone: "",
    areaNumber: "",
  });
  const [tickets, setTickets] = useState([]);
  const [matches, setMatches] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('aboor_token');
    const savedUser = localStorage.getItem('aboor_user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        socketService.connect();
        setScreen("matches");
      } catch (err) {
        console.error('Failed to load user:', err);
        localStorage.removeItem('aboor_token');
        localStorage.removeItem('aboor_user');
      }
    }
  }, []);

  // Fetch matches when needed
  useEffect(() => {
    if (screen === 'matches' && user && matches.length === 0) {
      fetchMatches();
    }
  }, [screen, user]);

  // Fetch user tickets
  useEffect(() => {
    if (screen === 'myTickets' && user) {
      fetchUserTickets();
    }
  }, [screen, user]);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/matches?status=upcoming');
      setMatches(response.data.matches);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      setError('فشل تحميل المباريات. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users/tickets');
      setTickets(response.data.tickets);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      setError('فشل تحميل التذاكر. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (identifier, password) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/auth/login', {
        identifier,
        password
      });

      // Save token and user
      localStorage.setItem('aboor_token', response.data.token);
      localStorage.setItem('aboor_user', JSON.stringify(response.data.user));
      setUser(response.data.user);

      // Connect WebSocket
      socketService.connect();

      setScreen("matches");
    } catch (error) {
      console.error('Login failed:', error);
      const message = error.response?.data?.message || 'فشل تسجيل الدخول. يرجى التحقق من البيانات.';
      setError(message);
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMatch = async (match) => {
    setSelectedMatch(match);
    socketService.joinMatch(match._id);
    setScreen("matchDetails");
  };

  const handleSeatChange = (field, value) => {
    setSeatInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleGoConfirm = () => {
    if (!seatInfo.zone || !seatInfo.areaNumber) {
      alert('يرجى اختيار المنطقة ورقم المقعد');
      return;
    }
    setScreen("confirm");
  };

  const handleConfirmBooking = async () => {
    if (!selectedMatch) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/tickets/book', {
        matchId: selectedMatch._id,
        seatInfo
      });

      setSelectedTicket(response.data.ticket);
      setScreen("payment");
    } catch (error) {
      console.error('Booking failed:', error);
      const message = error.response?.data?.message || 'فشل الحجز. يرجى المحاولة مرة أخرى.';
      setError(message);
      alert(message);
      // Go back to match details if booking failed
      setScreen("matchDetails");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    if (!selectedTicket) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.post(`/tickets/${selectedTicket._id}/payment`, {
        paymentMethod: 'mada'
      });

      // Update ticket with confirmed booking
      setSelectedTicket(response.data.ticket);
      setScreen("success");
    } catch (error) {
      console.error('Payment failed:', error);
      const message = error.response?.data?.message || 'فشل الدفع. يرجى المحاولة مرة أخرى.';
      setError(message);
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToMyTickets = () => {
    setScreen("myTickets");
  };

  const handleCancelTicket = async (ticketId) => {
    if (!window.confirm('هل أنت متأكد من إلغاء التذكرة؟')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await api.put(`/tickets/${ticketId}/cancel`);
      
      // Refresh tickets
      await fetchUserTickets();
      alert('تم إلغاء التذكرة بنجاح');
    } catch (error) {
      console.error('Cancel failed:', error);
      const message = error.response?.data?.message || 'فشل إلغاء التذكرة.';
      setError(message);
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('aboor_token');
    localStorage.removeItem('aboor_user');
    setUser(null);
    setTickets([]);
    setMatches([]);
    setSeatInfo({ zone: "", areaNumber: "" });
    setSelectedMatch(null);
    setSelectedTicket(null);
    socketService.disconnect();
    setScreen("landing");
  };

  const currentPrice = selectedMatch ? selectedMatch.basePrice : 0;

  return (
    <div className="app-root">
      <div className="phone-frame">
        {/* Loading overlay */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{ color: 'white', fontSize: '20px' }}>جاري التحميل...</div>
          </div>
        )}

        {/* Top bar with app title + menu */}
        {screen !== "landing" && screen !== "nafath" && (
          <header className="top-bar">
            <button
              className="icon-button"
              onClick={() => setSideMenuOpen(true)}
            >
              ☰
            </button>
            <div className="top-bar-title">
              <img
                src="/aboor-logo.png"
                alt="Aboor"
                className="top-bar-logo"
              />
            </div>
            <button
              className="icon-button"
              style={{width: "30px", height: "30px"}}
            >
              
            </button>

          </header>
        )}

        {/* SCREENS */}
        {screen === "landing" && (
          <LandingScreen onContinue={(method) => {
            setLoginMethod(method);
            setScreen("nafath");
          }} />
        )}

        {screen === "nafath" && (
          <NafathLoginScreen
            onBack={() => setScreen("landing")}
            onLogin={handleLogin}
            loading={loading}
            loginMethod={loginMethod}
          />
        )}

        {screen === "matches" && (
          <MatchListScreen
            matches={matches}
            onSelectMatch={handleSelectMatch}
            loading={loading}
            error={error}
            onRetry={fetchMatches}
          />
        )}

        {screen === "matchDetails" && selectedMatch && (
          <MatchDetailsScreen
            match={selectedMatch}
            seatInfo={seatInfo}
            onSeatChange={handleSeatChange}
            onNext={handleGoConfirm}
            onBack={() => {
              socketService.leaveMatch(selectedMatch._id);
              setScreen("matches");
            }}
          />
        )}

        {screen === "confirm" && selectedMatch && (
          <ConfirmScreen
            match={selectedMatch}
            seatInfo={seatInfo}
            price={currentPrice}
            onBack={() => setScreen("matchDetails")}
            onConfirm={handleConfirmBooking}
          />
        )}

        {screen === "payment" && selectedTicket && (
          <PaymentScreen
            price={currentPrice}
            ticket={selectedTicket}
            onBack={() => setScreen("confirm")}
            onPay={handlePaymentSuccess}
          />
        )}

        {screen === "success" && (
          <SuccessScreen onGoTickets={handleGoToMyTickets} />
        )}

        {screen === "myTickets" && (
          <MyTicketsScreen
            tickets={tickets}
            onBack={() => setScreen("matches")}
            onViewDetails={(ticket) => {
              setSelectedTicket(ticket);
              setScreen("ticketDetails");
            }}
            onCancelTicket={handleCancelTicket}
            loading={loading}
          />
        )}

        {screen === "profile" && user && (
          <ProfileScreen 
            user={user} 
            onBack={() => setScreen("matches")} 
            onUserUpdate={(updatedUser) => {
              setUser(updatedUser);
              localStorage.setItem('aboor_user', JSON.stringify(updatedUser));
            }}
          />
        )}

        {screen === "ticketDetails" && selectedTicket && (
          <TicketDetailsScreen
            ticket={selectedTicket}
            user={user}
            onBack={() => setScreen("myTickets")}
          />
        )}

        {/* SIDE MENU OVERLAY */}
        {sideMenuOpen && user && (
          <SideMenu
            user={user}
            onClose={() => setSideMenuOpen(false)}
            onMyTickets={() => {
              setSideMenuOpen(false);
              setScreen("myTickets");
            }}
            onProfile={() => {
              setSideMenuOpen(false);
              setScreen("profile");
            }}
            onHome={() => {
              setSideMenuOpen(false);
              setScreen("matches");
            }}
            onLogout={() => {
              setSideMenuOpen(false);
              handleLogout();
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------------- Screens ---------------------- */

function LandingScreen({ onContinue }) {
  return (
    <div className="screen landing-screen">
      <div className="landing-card">
        <img src="/aboor-logo.png" alt="Aboor" className="landing-logo" />
        <h1 className="landing-title">منصة عبور</h1>
        <p className="landing-description">
          اختر طريقة تسجيل الدخول للتحقق من هويتك وإدارة تذاكرك بسهولة.
        </p>
        <button className="primary-button nafath-button" onClick={() => onContinue('nafath')}>
          تسجيل الدخول عبر نفاذ
        </button>
        <button className="primary-button muqeem-button" onClick={() => onContinue('muqeem')}>
          تسجيل الدخول عن طريق مقيم
        </button>
      </div>
    </div>
  );
}

function NafathLoginScreen({ onLogin, onBack, loading, loginMethod = 'nafath' }) {
  const [identifier, setIdentifier] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [verificationNumber, setVerificationNumber] = useState('');
  const [countdown, setCountdown] = useState(10);
  const password = '12341234'; // Hardcoded password
  
  // Branding configuration based on login method
  const isMuqeem = loginMethod === 'muqeem';
  const brandConfig = {
    logoText: isMuqeem ? 'Muqeem' : 'نفاذ',
    subtitle: isMuqeem ? 'نظام مقيم' : 'النفاذ الوطني الموحد',
    appText: isMuqeem ? 'الرجاء فتح تطبيق مقيم وتأكيد الطلب بإختيار الرقم' : 'الرجاء فتح تطبيق نفاذ وتأكيد الطلب بإختيار الرقم',
    color: isMuqeem ? '#ff6600' : '#16a085',
    gradient: isMuqeem ? 'linear-gradient(135deg, #ff6600 0%, #cc5200 100%)' : 'linear-gradient(135deg, #16a085 0%, #0d8270 100%)',
    shadowColor: isMuqeem ? 'rgba(255, 102, 0, 0.3)' : 'rgba(22, 160, 133, 0.3)'
  };

  const handleSubmit = () => {
    if (!identifier) {
      alert('يرجى إدخال رقم الهوية');
      return;
    }
    
    // Generate random 2-digit number
    const randomNum = Math.floor(Math.random() * 90) + 10; // 10-99
    setVerificationNumber(randomNum.toString());
    setShowVerification(true);
    setCountdown(10);
  };

  // Countdown timer effect
  React.useEffect(() => {
    if (showVerification && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showVerification && countdown === 0) {
      // Auto proceed to login after countdown
      onLogin(identifier, password);
    }
  }, [showVerification, countdown, identifier, password, onLogin]);

  // Show verification screen
  if (showVerification) {
    return (
      <div className={`screen nafath-screen ${isMuqeem ? 'muqeem-theme' : ''}`}>
        <button className="nafath-back" onClick={() => setShowVerification(false)}>
          ← العودة
        </button>
        <div className="nafath-card">
          <div className="nafath-header">
            <div className="nafath-logo-word" style={{ color: brandConfig.color }}>
              {brandConfig.logoText}
            </div>
            <div className="nafath-subtitle">{brandConfig.subtitle}</div>
          </div>
          
          <div style={{textAlign: 'center', padding: '20px 0'}}>
            <p style={{fontSize: '16px', color: '#333', marginBottom: '30px', lineHeight: '1.8'}}>
              {brandConfig.appText}
            </p>
            
            <div style={{
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: brandConfig.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px',
              boxShadow: `0 8px 24px ${brandConfig.shadowColor}`
            }}>
              <span style={{
                fontSize: '64px',
                fontWeight: 'bold',
                color: 'white'
              }}>
                {verificationNumber}
              </span>
            </div>
            
            <p style={{fontSize: '18px', color: brandConfig.color, fontWeight: 'bold', marginBottom: '20px'}}>
              الوقت المتبقي ({countdown}) ثانية
            </p>
            
            <p style={{fontSize: '14px', color: '#666', lineHeight: '1.6'}}>
              لتتمكن من الدخول بسهولة قم بتحميل تطبيق {isMuqeem ? 'مقيم' : 'نفاذ'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show login form
  return (
    <div className={`screen nafath-screen ${isMuqeem ? 'muqeem-theme' : ''}`}>
      <button className="nafath-back" onClick={onBack}>
        ← العودة
      </button>
      <div className="nafath-card">
        <div className="nafath-header">
          <div className="nafath-logo-word" style={{ color: brandConfig.color }}>
            {brandConfig.logoText}
          </div>
          <div className="nafath-subtitle">{brandConfig.subtitle}</div>
          <p>مرحباً بك في منصة عبور</p>
        </div>
        <div className="nafath-section-title">الدخول</div>
        
        <label className="nafath-label">رقم الهوية *</label>
        <input
          type="text"
          className="nafath-input"
          placeholder="أدخل رقم الهوية"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
          disabled={loading}
        />

        <button 
          className={`nafath-login-button ${isMuqeem ? 'muqeem-button' : ''}`}
          onClick={handleSubmit}
          disabled={loading}
          style={isMuqeem ? { background: brandConfig.color } : {}}
        >
          {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
        </button>

        <div className="nafath-test-accounts" style={{marginTop: '20px', fontSize: '11px', color: '#666', textAlign: 'center', padding: '10px', background: '#f5f5f5', borderRadius: '8px'}}>
          <p style={{fontWeight: 'bold', marginBottom: '5px'}}>حسابات تجريبية:</p>
          {/* <p style={{margin: '3px 0'}}>Email: waleed@example.com</p> */}
          <p style={{margin: '3px 0'}}>National ID: 1234567890</p>
        </div>
      </div>
    </div>
  );
}

function MatchListScreen({ matches, onSelectMatch, loading, error, onRetry }) {
  return (
    <div className="screen">
      <h2 className="screen-title">المباريات المتاحة</h2>
      
      {error && (
        <div style={{padding: '15px', background: '#ffebee', color: '#c62828', borderRadius: '8px', margin: '10px 0'}}>
          <p>{error}</p>
          <button onClick={onRetry} className="secondary-button" style={{marginTop: '10px'}}>
            إعادة المحاولة
          </button>
        </div>
      )}

      {loading && !matches.length ? (
        <p className="hint-text">جاري تحميل المباريات...</p>
      ) : matches.length === 0 ? (
        <p className="hint-text">لا توجد مباريات متاحة حالياً.</p>
      ) : (
        <div className="match-list">
          {matches.map((match) => (
            <button
              key={match._id}
              className="match-card"
              onClick={() => onSelectMatch(match)}
            >
              {match.time && (
                <div className="match-time">{match.time}</div>
              )}
              <div className="match-card-content">
                <div className="match-team-section">
                  <img 
                    src={match.homeTeamLogo} 
                    alt={match.homeTeam}
                    className="team-logo"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/100x100/667eea/ffffff?text=${encodeURIComponent(match.homeTeamIcon)}`;
                    }}
                  />
                  <div className="team-name">{match.homeTeam}</div>
                </div>
                
                <div className="match-vs-section">
                  <div className="vs-text">VS</div>
                </div>
                
                <div className="match-team-section">
                  <img 
                    src={match.awayTeamLogo} 
                    alt={match.awayTeam}
                    className="team-logo"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/100x100/667eea/ffffff?text=${encodeURIComponent(match.awayTeamIcon)}`;
                    }}
                  />
                  <div className="team-name">{match.awayTeam}</div>
                </div>
              </div>
              <div className="match-meta">
                <span>{new Date(match.date).toLocaleDateString('ar-SA')}</span>
                <span>{match.stadium}</span>
                {match.availableSeats !== undefined && (
                  <span>{match.availableSeats} مقعد متاح</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MatchDetailsScreen({ match, seatInfo, onSeatChange, onNext, onBack }) {
  useEffect(() => {
    if (match?._id) {
      // Listen for real-time updates
      socketService.onSeatsUpdated(() => {
        console.log('Seats updated for match');
        // Could refetch match details here
      });

      return () => {
        socketService.removeAllListeners();
      };
    }
  }, [match]);

  return (
    <div className="screen">
      <div className="screen-header-with-back">
        <button className="back-button-icon" onClick={onBack}>
          ←
        </button>
        <h2 className="screen-title">تفاصيل المباراة</h2>
      </div>
      <div className="match-header">
        <div className="match-header-team">
          <img 
            src={match.homeTeamLogo} 
            alt={match.homeTeam}
            className="team-logo-large"
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/100x100/667eea/ffffff?text=${encodeURIComponent(match.homeTeamIcon)}`;
            }}
          />
          <div className="team-name-large">{match.homeTeam}</div>
        </div>
        <span className="vs-inline-large">VS</span>
        <div className="match-header-team">
          <img 
            src={match.awayTeamLogo} 
            alt={match.awayTeam}
            className="team-logo-large"
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/100x100/667eea/ffffff?text=${encodeURIComponent(match.awayTeamIcon)}`;
            }}
          />
          <div className="team-name-large">{match.awayTeam}</div>
        </div>
      </div>

      {/* Match info */}
      <div className="match-info">
        <div className="match-info-item">
          <span className="match-info-icon">📅</span>
          <span className="match-info-label">التاريخ:</span>
          <span className="match-info-value">{new Date(match.date).toLocaleDateString('ar-SA')}</span>
        </div>
        <div className="match-info-item">
          <span className="match-info-icon">🏟️</span>
          <span className="match-info-label">الملعب:</span>
          <span className="match-info-value">{match.stadium}</span>
        </div>
        {match.time && (
          <div className="match-info-item">
            <span className="match-info-icon">⏰</span>
            <span className="match-info-label">الوقت:</span>
            <span className="match-info-value">{match.time}</span>
          </div>
        )}
        {match.availableSeats !== undefined && (
          <div className="match-info-item">
            <span className="match-info-icon">💺</span>
            <span className="match-info-label">المقاعد المتاحة:</span>
            <span className="match-info-value">{match.availableSeats}</span>
          </div>
        )}
      </div>

      {/* Stadium Map */}
      <div className="stadium-map">
        <img 
          src="/image.png" 
          alt="Stadium Seating Chart" 
          className="stadium-image"
        />
      </div>

      {/* Area Selection */}
      <div className="seat-form">
        <label className="field-label">لون المنطقة</label>
        <select
          className="input"
          value={seatInfo.zone}
          onChange={(e) => onSeatChange("zone", e.target.value)}
        >
          <option value="">اختر لون المنطقة</option>
          <option value="Red">أحمر (Red)</option>
          <option value="Yellow">أصفر (Yellow)</option>
          <option value="Green">أخضر (Green)</option>
          <option value="Blue">أزرق (Blue)</option>
          <option value="Pink">وردي (Pink)</option>
          <option value="Orange">برتقالي (Orange)</option>
          <option value="Cyan">سماوي (Cyan)</option>
        </select>

        <label className="field-label">رقم المنطقة</label>
        <select
          className="input"
          value={seatInfo.areaNumber || ""}
          onChange={(e) => onSeatChange("areaNumber", e.target.value)}
        >
          <option value="">اختر رقم المنطقة</option>
          <option value="104">104</option>
          <option value="105">105</option>
          <option value="106">106</option>
          <option value="107">107</option>
          <option value="108">108</option>
          <option value="109">109</option>
          <option value="110">110</option>
          <option value="111">111</option>
          <option value="112">112</option>
          <option value="113">113</option>
          <option value="114">114</option>
          <option value="115">115</option>
          <option value="116">116</option>
          <option value="119">119</option>
          <option value="120">120</option>
          <option value="121">121</option>
          <option value="125">125</option>
          <option value="126">126</option>
          <option value="130">130</option>
          <option value="131">131</option>
          <option value="132">132</option>
          <option value="133">133</option>
          <option value="134">134</option>
          <option value="135">135</option>
          <option value="136">136</option>
          <option value="137">137</option>
          <option value="138">138</option>
          <option value="139">139</option>
          <option value="140">140</option>
          <option value="201">201</option>
          <option value="202">202</option>
          <option value="203">203</option>
          <option value="204">204</option>
          <option value="205">205</option>
          <option value="206">206</option>
          <option value="207">207</option>
          <option value="208">208</option>
          <option value="209">209</option>
          <option value="210">210</option>
          <option value="211">211</option>
          <option value="212">212</option>
          <option value="213">213</option>
          <option value="214">214</option>
          <option value="215">215</option>
          <option value="216">216</option>
          <option value="217">217</option>
          <option value="218">218</option>
          <option value="219">219</option>
          <option value="220">220</option>
          <option value="221">221</option>
          <option value="222">222</option>
          <option value="223">223</option>
          <option value="224">224</option>
          <option value="225">225</option>
          <option value="226">226</option>
        </select>
      </div>

      {/* Price + ticket icon */}
      <div className="price-row">
        <div className="price-text">{match.basePrice} SAR</div>
        <div className="ticket-icon">🎟️</div>
      </div>

      <button className="primary-button" onClick={onNext}>
        NEXT
      </button>
    </div>
  );
}

function ConfirmScreen({ match, seatInfo, price, onBack, onConfirm }) {
  return (
    <div className="screen">
      <h2 className="screen-title">تأكيد الحجز</h2>

      <InfoBox label="المباراة">
        <div className="info-team-row-logos">
          <div className="info-team">
            <img
              src={match.homeTeamLogo || getTeamLogo(match.homeTeam)}
              alt={match.homeTeam}
              className="info-team-logo"
              onError={(e) => {
                e.target.src = getTeamLogo(match.homeTeam);
              }}
            />
            <span>{match.homeTeam}</span>
          </div>
          <span className="vs-inline">VS</span>
          <div className="info-team">
            <img
              src={match.awayTeamLogo || getTeamLogo(match.awayTeam)}
              alt={match.awayTeam}
              className="info-team-logo"
              onError={(e) => {
                e.target.src = getTeamLogo(match.awayTeam);
              }}
            />
            <span>{match.awayTeam}</span>
          </div>
        </div>
      </InfoBox>

      <InfoBox label="التاريخ">{new Date(match.date).toLocaleDateString('ar-SA')}</InfoBox>

      <InfoBox label="المدرج / المنطقة">
        {seatInfo.zone && seatInfo.areaNumber 
          ? `${seatInfo.zone} - Area ${seatInfo.areaNumber}`
          : "لم يتم اختيار المنطقة"}
      </InfoBox>

      <InfoBox label="المبلغ">{price.toFixed(2)} SAR</InfoBox>

      <div className="button-row">
        <button className="secondary-button" onClick={onBack}>
          رجوع
        </button>
        <button className="primary-button" onClick={onConfirm}>
          تأكيد الحجز
        </button>
      </div>
    </div>
  );
}

function PaymentScreen({ price, ticket, onBack, onPay }) {
  return (
    <div className="screen payment-screen">
      <h2 className="screen-title">الدفع</h2>

      <div className="payment-price">
        <span className="payment-amount">{price} SAR</span>
        <span className="ticket-icon-large">🎟️</span>
      </div>

      {ticket?.reservedUntil && (
        <div style={{textAlign: 'center', color: '#ff9800', margin: '10px 0', fontSize: '14px'}}>
          <p>الحجز صالح حتى: {new Date(ticket.reservedUntil).toLocaleTimeString('ar-SA')}</p>
        </div>
      )}

      <div className="payment-methods">
        <span>mada</span>
        <span>VISA</span>
        <span>MasterCard</span>
        <span>Apple Pay</span>
        <span>STC Pay</span>
        <span>PayPal</span>
        <span>تمارا</span>
      </div>

      <div className="button-row">
        <button className="secondary-button" onClick={onBack}>
          رجوع
        </button>
        <button className="applepay-button" onClick={onPay}>
          إتمام الدفع
        </button>
      </div>
    </div>
  );
}

function SuccessScreen({ onGoTickets }) {
  return (
    <div className="screen success-screen">
      <h2 className="success-title">تم الحجز بنجاح!</h2>
      <div className="success-emoji">🥳</div>
      <p style={{textAlign: 'center', color: '#666', margin: '15px 0'}}>
        تم إرسال تأكيد الحجز إلى بريدك الإلكتروني
      </p>
      <button className="primary-button" onClick={onGoTickets}>
        الذهاب إلى التذاكر
      </button>
    </div>
  );
}

function MyTicketsScreen({ tickets, onBack, onViewDetails, onCancelTicket, loading }) {
  return (
    <div className="screen">
      <h2 className="screen-title">تذاكري</h2>

      {loading && !tickets.length ? (
        <p className="hint-text">جاري تحميل التذاكر...</p>
      ) : tickets.length === 0 ? (
        <p className="hint-text">لا توجد تذاكر حتى الآن.</p>
      ) : (
        <div className="ticket-list">
          {tickets.map((t) => {
            const match = t.matchId;
            return (
              <div key={t._id} className="ticket-card">
                <div className="ticket-card-content">
                  <div className="ticket-card-team">
                    <img
                      src={match.homeTeamLogo || getTeamLogo(match.homeTeam)}
                      alt={match.homeTeam}
                      className="ticket-team-logo"
                      onError={(e) => {
                        e.target.src = getTeamLogo(match.homeTeam);
                      }}
                    />
                    <div className="ticket-team-name">{match.homeTeam}</div>
                  </div>
                  <div className="ticket-card-vs">VS</div>
                  <div className="ticket-card-team">
                    <img
                      src={match.awayTeamLogo || getTeamLogo(match.awayTeam)}
                      alt={match.awayTeam}
                      className="ticket-team-logo"
                      onError={(e) => {
                        e.target.src = getTeamLogo(match.awayTeam);
                      }}
                    />
                    <div className="ticket-team-name">{match.awayTeam}</div>
                  </div>
                </div>

                <div className="ticket-meta">
                  <span>{match.stadium}</span>
                  <span>{new Date(match.date).toLocaleDateString('ar-SA')}</span>
                </div>

                <div className="ticket-seat-info">
                  {t.seatInfo.zone && t.seatInfo.areaNumber
                    ? `${t.seatInfo.zone} - Area ${t.seatInfo.areaNumber}`
                    : "لم يتم اختيار المنطقة"}
                </div>

                <div style={{fontSize: '12px', color: '#666', textAlign: 'center', margin: '5px 0'}}>
                  {t.status === 'active' && '✅ نشطة'}
                  {t.status === 'reserved' && '⏳ محجوزة'}
                  {t.status === 'cancelled' && '❌ ملغاة'}
                </div>

                <div className="ticket-actions">
                  <button
                    className="secondary-button small"
                    onClick={() => onViewDetails(t)}
                  >
                    التفاصيل
                  </button>
                  {(t.status === 'active' || t.status === 'reserved') && (
                    <button 
                      className="secondary-button small"
                      onClick={() => onCancelTicket(t._id)}
                    >
                      إلغاء التذكرة
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button className="secondary-button" onClick={onBack}>
        رجوع إلى المباريات
      </button>
    </div>
  );
}

const QR_PATTERN = [
  [1, 1, 1, 1, 1, 0, 1, 0, 0, 1, 1, 1],
  [1, 0, 0, 0, 1, 0, 1, 1, 0, 0, 0, 1],
  [1, 0, 1, 0, 1, 1, 0, 1, 1, 1, 0, 1],
  [1, 0, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1],
  [1, 1, 1, 1, 1, 0, 0, 1, 0, 1, 1, 1],
  [0, 0, 0, 0, 0, 1, 1, 0, 1, 0, 0, 0],
  [1, 1, 0, 1, 0, 1, 0, 1, 1, 1, 1, 0],
  [0, 1, 0, 1, 1, 0, 1, 0, 0, 0, 1, 0],
  [1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0, 1],
  [0, 1, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0],
  [1, 0, 0, 0, 1, 1, 0, 1, 0, 1, 1, 1],
  [1, 1, 1, 1, 0, 0, 1, 1, 0, 0, 0, 1],
];

function TicketDetailsScreen({ ticket, user, onBack }) {
  const [activeQRCode, setActiveQRCode] = useState(ticket.activeQRCode || 'primary');
  const [showSwitchNotification, setShowSwitchNotification] = useState(false);
  const [ticketStatus, setTicketStatus] = useState(ticket.status);
  const [showSuccessBanner, setShowSuccessBanner] = useState(ticket.status === 'used');
  
  const match = ticket.matchId;
  const seatLabel =
    ticket.seatInfo.zone && ticket.seatInfo.areaNumber
      ? `${ticket.seatInfo.zone} - Area ${ticket.seatInfo.areaNumber}`
      : "لم يتم اختيار المنطقة";

  // Ensure WebSocket is connected
  useEffect(() => {
    const socket = socketService.connect();
    console.log('🔌 WebSocket status:', socket?.connected ? 'Connected' : 'Disconnected');
  }, []);

  // Polling: Check ticket status every 3 seconds for updates
  useEffect(() => {
    let pollInterval;
    
    // Poll if ticket is still active or in secondary state
    if (ticketStatus !== 'used') {
      console.log('🔄 Starting ticket status polling...');
      
      pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`/tickets/${ticket._id}`);
          const updatedTicket = response.data.ticket;
          
          console.log('📊 Polled ticket status:', {
            activeQRCode: updatedTicket.activeQRCode,
            status: updatedTicket.status
          });
          
          // Check if QR code switched to secondary
          if (updatedTicket.activeQRCode === 'secondary' && activeQRCode === 'primary') {
            console.log('🎉 Ticket switched to secondary via polling!');
            setActiveQRCode('secondary');
            setShowSwitchNotification(true);
            setTimeout(() => setShowSwitchNotification(false), 5000);
          }
          
          // Check if ticket was used (both QR codes scanned successfully!)
          if (updatedTicket.status === 'used' && ticketStatus !== 'used') {
            console.log('✅✅ ENTRY GRANTED! Both QR codes scanned successfully!');
            setTicketStatus('used');
            setShowSuccessBanner(true);
            
            // Play success sound
            if (typeof Audio !== 'undefined') {
              try {
                const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGJ0fPTgjMGHm7A7+OZSBAQY7bt7qNbGAg+lvDyvGkhBSqA1vPRfzMGIHPD8OKWSREQY7Xr7KBYGAg/mfDy');
                audio.play().catch(e => console.log('Could not play sound'));
              } catch (e) {
                console.log('Audio not supported');
              }
            }
          }
        } catch (error) {
          console.error('❌ Error polling ticket status:', error);
        }
      }, 3000); // Poll every 3 seconds
    } else {
      console.log('✅ Ticket used - stopping polling');
    }

    return () => {
      if (pollInterval) {
        console.log('🛑 Stopping ticket status polling');
        clearInterval(pollInterval);
      }
    };
  }, [activeQRCode, ticket._id, ticketStatus]);

  // Listen for QR code switch events
  useEffect(() => {
    if (!user) return;

    const eventName = `qr-switch-${user._id}`;
    console.log(`👂 Listening for QR switch events on: ${eventName}`);

    const handleQRSwitch = (data) => {
      console.log('🎉 QR Code switch event received:', data);
      console.log('Current ticket ID:', ticket._id);
      console.log('Event ticket ID:', data.ticketId);
      
      // Convert both to strings for comparison
      if (data.ticketId?.toString() === ticket._id?.toString()) {
        console.log('✅ QR Code switching to secondary!');
        setActiveQRCode('secondary');
        setShowSwitchNotification(true);
        
        // Play a sound notification (optional)
        if (typeof Audio !== 'undefined') {
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGJ0fPTgjMGHm7A7+OZSBAQY7bt7qNbGAg+lvDyvGkhBSqA1vPRfzMGIHPD8OKWSREQY7Xr7KBYGAg/mfDy'); 
            audio.play().catch(e => console.log('Could not play notification sound'));
          } catch (e) {
            console.log('Audio not supported');
          }
        }
        
        // Hide notification after 5 seconds
        setTimeout(() => {
          setShowSwitchNotification(false);
        }, 5000);
      } else {
        console.log('❌ Ticket ID mismatch - event not for this ticket');
      }
    };

    // Listen for switch event for this user
    socketService.on(eventName, handleQRSwitch);

    return () => {
      console.log(`🔇 Removing listener for: ${eventName}`);
      socketService.removeListener(eventName, handleQRSwitch);
    };
  }, [ticket._id, user]);

  return (
    <div className="screen">
      <div className="screen-header-with-back">
        <button className="back-button-icon" onClick={onBack}>
          ←
        </button>
        <h2 className="screen-title">تفاصيل التذكرة</h2>
      </div>

      <div className="ticket-details-card">
        <div className="ticket-details-match">
          <div className="match-header-team">
            <img
              src={match.homeTeamLogo || getTeamLogo(match.homeTeam)}
              alt={match.homeTeam}
              className="team-logo-large"
              onError={(e) => {
                e.target.src = getTeamLogo(match.homeTeam);
              }}
            />
            <div className="team-name-large">{match.homeTeam}</div>
          </div>
          <span className="vs-inline-large">VS</span>
          <div className="match-header-team">
            <img
              src={match.awayTeamLogo || getTeamLogo(match.awayTeam)}
              alt={match.awayTeam}
              className="team-logo-large"
              onError={(e) => {
                e.target.src = getTeamLogo(match.awayTeam);
              }}
            />
            <div className="team-name-large">{match.awayTeam}</div>
          </div>
        </div>

        <div className="ticket-detail-info">
          <span>التاريخ</span>
          <strong>{new Date(match.date).toLocaleDateString('ar-SA')}</strong>
        </div>
        <div className="ticket-detail-info">
          <span>الملعب</span>
          <strong>{match.stadium}</strong>
        </div>
        <div className="ticket-detail-info">
          <span>المقعد</span>
          <strong>{seatLabel}</strong>
        </div>
        <div className="ticket-detail-info">
          <span>السعر</span>
          <strong>{ticket.price} SAR</strong>
        </div>
        <div className="ticket-detail-info">
          <span>الحالة</span>
          <strong>
            {ticket.status === 'active' && 'نشطة ✅'}
            {ticket.status === 'reserved' && 'محجوزة ⏳'}
            {ticket.status === 'cancelled' && 'ملغاة ❌'}
          </strong>
        </div>

        {/* QR Code Section - Single display */}
        <div className="ticket-qr-section">
          {/* Success Banner - Shows when both QR codes scanned */}
          {showSuccessBanner && (
            <div className="entry-granted-banner">
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                تم السماح بالدخول!
              </div>
              <div style={{ fontSize: '14px', opacity: '0.9' }}>
                تم المسح بنجاح • QR codes verified
              </div>
            </div>
          )}
          
          {showSwitchNotification && !showSuccessBanner && (
            <div className="qr-switch-notification animate-pulse">
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎉</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>تم مسح الرمز الأول بنجاح!</div>
              <div style={{ fontSize: '16px', marginTop: '8px' }}>👇 يرجى إظهار الرمز الثاني أدناه عند البوابة</div>
            </div>
          )}
          
          {/* Manual refresh button */}
          <div style={{ textAlign: 'center', margin: '15px 0' }}>
            <button 
              onClick={() => {
                console.log('🔄 Manual refresh - checking activeQRCode:', ticket.activeQRCode);
                if (ticket.activeQRCode === 'secondary') {
                  setActiveQRCode('secondary');
                  setShowSwitchNotification(true);
                  setTimeout(() => setShowSwitchNotification(false), 3000);
                }
              }}
              style={{
                padding: '10px 20px',
                background: '#0d8191',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              🔄 تحديث حالة التذكرة
            </button>
          </div>
          
          <div className="ticket-qr-title">
            {activeQRCode === 'primary' ? 'رمز الدخول - QR Code 1' : 'رمز البوابة - QR Code 2'}
          </div>
          
          <div className="ticket-single-qr">
            {activeQRCode === 'primary' ? (
              // Primary QR Code
              <div className="ticket-qr-display">
                <div className="qr-stage-label">🚪 للمسح عند المدخل الرئيسي</div>
                {ticket.qrCodeImage1 ? (
                  <img 
                    src={ticket.qrCodeImage1} 
                    alt="Ticket QR Code 1" 
                    className="ticket-qr-image-large"
                  />
                ) : (
                  <div className="ticket-qr-grid-large">
                    {QR_PATTERN.map((row, rowIndex) =>
                      row.map((cell, cellIndex) => (
                        <span
                          key={`qr1-${rowIndex}-${cellIndex}`}
                          className={`qr-cell ${cell ? "filled" : ""}`}
                        />
                      ))
                    )}
                  </div>
                )}
                <div className="ticket-qr-id-large">{ticket.qrCode1 || `AB${String(ticket._id).slice(-6)}`}</div>
                <div className="qr-instruction">⏳ سيظهر الرمز الثاني بعد المسح</div>
              </div>
            ) : (
              // Secondary QR Code
              <div className="ticket-qr-display">
                <div className="qr-stage-label">🎫 للمسح عند بوابة المقعد</div>
                {ticket.qrCodeImage2 ? (
                  <img 
                    src={ticket.qrCodeImage2} 
                    alt="Ticket QR Code 2" 
                    className="ticket-qr-image-large"
                  />
                ) : (
                  <div className="ticket-qr-grid-large">
                    {QR_PATTERN.map((row, rowIndex) =>
                      row.map((cell, cellIndex) => (
                        <span
                          key={`qr2-${rowIndex}-${cellIndex}`}
                          className={`qr-cell ${cell ? "filled" : ""}`}
                        />
                      ))
                    )}
                  </div>
                )}
                <div className="ticket-qr-id-large">{ticket.qrCode2 || `AB${String(ticket._id).slice(-6)}`}</div>
                <div className="qr-scanned-time">
                  ✅ الرمز الأول تم مسحه: {ticket.qrCode1ScannedAt ? new Date(ticket.qrCode1ScannedAt).toLocaleTimeString('ar-SA') : 'للتو'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileScreen({ user, onBack, onUserUpdate }) {
  const [dependents, setDependents] = useState(user.dependents || []);
  const [showAddDependent, setShowAddDependent] = useState(false);
  const [newDependent, setNewDependent] = useState({ name: '', nationalId: '' });
  const [loading, setLoading] = useState(false);

  const handleAddDependent = async () => {
    if (!newDependent.name.trim() || !newDependent.nationalId.trim()) {
      alert('يرجى إدخال الاسم ورقم الهوية');
      return;
    }
    
    // Validate national ID format
    if (!/^\d{10}$/.test(newDependent.nationalId)) {
      alert('رقم الهوية يجب أن يكون 10 أرقام');
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/users/dependents', {
        name: newDependent.name.trim(),
        nationalId: newDependent.nationalId
      });

      if (response.data.success) {
        // Update local state with backend response
        setDependents(response.data.user.dependents);
        setNewDependent({ name: '', nationalId: '' });
        setShowAddDependent(false);
        
        // Update user in parent component
        if (onUserUpdate) {
          onUserUpdate(response.data.user);
        }
        
        alert('تم إضافة التابع بنجاح ✓');
      }
    } catch (error) {
      console.error('Failed to add dependent:', error);
      const errorMessage = error.response?.data?.message || 'فشل في إضافة التابع';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDependent = async (dependentId) => {
    if (!window.confirm('هل تريد حذف هذا التابع؟')) {
      return;
    }

    setLoading(true);

    try {
      const response = await api.delete(`/users/dependents/${dependentId}`);

      if (response.data.success) {
        // Update local state with backend response
        setDependents(response.data.user.dependents);
        
        // Update user in parent component
        if (onUserUpdate) {
          onUserUpdate(response.data.user);
        }
        
        alert('تم حذف التابع بنجاح ✓');
      }
    } catch (error) {
      console.error('Failed to remove dependent:', error);
      const errorMessage = error.response?.data?.message || 'فشل في حذف التابع';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen profile-screen">
      <div className="screen-header-with-back">
        <button className="back-button-icon" onClick={onBack}>
          ←
        </button>
        <h2 className="screen-title">الملف الشخصي</h2>
      </div>

      <div className="profile-card">
        <div className="profile-avatar">{user.name.substring(0, 2).toUpperCase()}</div>
        <div className="profile-name">{user.name}</div>
        <div className="profile-email">{user.email}</div>
      </div>

      <div className="profile-info-grid">
        <div className="profile-info-item">
          <div className="profile-info-label">رقم الهوية</div>
          <div className="profile-info-value">{user.nationalId}</div>
          <div className="profile-info-label">رقم الجوال</div>
          <div className="profile-info-value">{user.phone}</div>
          <div className="profile-info-label">البريد الإلكتروني</div>
          <div className="profile-info-value profile-info-email">{user.email}</div>
        </div>
        <div className="profile-info-item">
          <div className="profile-info-label">حالة الحساب</div>
          <div className="profile-info-status">{user.isActive ? 'مفعل' : 'غير مفعل'}</div>
        </div>
      </div>

      <div className="profile-section">
        <div className="profile-section-title">التفضيلات</div>
        <div className="profile-preference">
          <span>الفريق المفضل</span>
          <span className="profile-tag">{user.favoriteTeam || 'لم يتم التحديد'}</span>
        </div>
        <div className="profile-preference">
          <span>الدور</span>
          <span className="profile-tag">{user.role === 'admin' ? 'مدير' : 'مستخدم'}</span>
        </div>
      </div>

      {/* التابعين Section */}
      <div className="profile-section">
        <div className="profile-section-title">التابعين</div>
        
        {/* Add Dependent Button */}
        {!showAddDependent && (
          <button 
            className="add-dependent-button"
            onClick={() => setShowAddDependent(true)}
          >
            + إضافة تابع
          </button>
        )}

        {/* Add Dependent Form */}
        {showAddDependent && (
          <div className="add-dependent-form">
            <label className="field-label">اسم التابع</label>
            <input
              type="text"
              className="input"
              placeholder="أدخل الاسم الكامل"
              value={newDependent.name}
              onChange={(e) => setNewDependent({...newDependent, name: e.target.value})}
            />
            
            <label className="field-label">رقم الهوية</label>
            <input
              type="text"
              className="input"
              placeholder="أدخل رقم الهوية"
              value={newDependent.nationalId}
              onChange={(e) => setNewDependent({...newDependent, nationalId: e.target.value})}
            />
            
            <div className="button-row">
              <button 
                className="primary-button"
                onClick={handleAddDependent}
                disabled={loading}
              >
                {loading ? 'جاري الإضافة...' : 'إضافة'}
              </button>
              <button 
                className="secondary-button"
                onClick={() => {
                  setShowAddDependent(false);
                  setNewDependent({ name: '', nationalId: '' });
                }}
                disabled={loading}
              >
                إلغاء
              </button>
            </div>
          </div>
        )}

        {/* List of Dependents */}
        {dependents.length > 0 && (
          <div className="dependents-list">
            {dependents.map((dependent) => (
              <div key={dependent._id || dependent.id} className="dependent-card">
                <div className="dependent-info">
                  <div className="dependent-name">{dependent.name}</div>
                  <div className="dependent-id">رقم الهوية: {dependent.nationalId}</div>
                </div>
                <button 
                  className="remove-dependent-button"
                  onClick={() => handleRemoveDependent(dependent._id || dependent.id)}
                  title="حذف التابع"
                  disabled={loading}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {dependents.length === 0 && !showAddDependent && (
          <div className="empty-dependents">
            <p>لا يوجد تابعين حالياً</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------- Shared components ---------------------- */

function InfoBox({ label, children }) {
  return (
    <div className="info-box">
      <div className="info-label">{label}</div>
      <div className="info-value">{children}</div>
    </div>
  );
}

function SideMenu({ user, onClose, onMyTickets, onHome, onProfile, onLogout }) {
  return (
    <div className="side-menu-overlay" onClick={onClose}>
      <div
        className="side-menu"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="side-menu-header">
          <button className="icon-button side-menu-close" onClick={onClose}>
            ✕
          </button>
          <div className="side-menu-profile-block">
            <div className="side-menu-avatar">{user.name.substring(0, 2).toUpperCase()}</div>
            <div className="side-menu-user-name">{user.name}</div>
            <div className="side-menu-user-email">{user.email}</div>
            <button className="side-menu-profile-button" onClick={onProfile}>
              الملف الشخصي
            </button>
          </div>
        </div>
        <button className="side-menu-item" onClick={onHome}>
          المباريات
        </button>
        <button className="side-menu-item" onClick={onMyTickets}>
          تذاكري
        </button>
        <button className="side-menu-item" onClick={onLogout}>
          تسجيل الخروج
        </button>
      </div>
    </div>
  );
}
